// Enhanced template system for better developer experience
export interface HtmlElement {
  tag: string;
  attributes?: Record<string, string>;
  children?: (HtmlElement | string)[];
  content?: string;
}

export class PrintTemplate {
  // Template helper functions with IntelliSense support
  static div(
    attributes: Record<string, string> = {},
    children: (HtmlElement | string)[] = [],
  ): HtmlElement {
    return { tag: "div", attributes, children };
  }

  static span(
    text: string,
    className?: string,
    attributes: Record<string, string> = {},
  ): HtmlElement {
    const attrs = className ? { class: className, ...attributes } : attributes;
    return { tag: "span", attributes: attrs, content: text };
  }

  static flexContainer(
    direction: "row" | "column" = "row",
    gap: string = "8px",
    alignItems: string = "center",
    justifyContent: string = "center",
    additionalStyles: string = "",
    children: (HtmlElement | string)[] = [],
  ): HtmlElement {
    const style = `display: flex; flex-direction: ${direction}; gap: ${gap}; align-items: ${alignItems}; justify-content: ${justifyContent}; ${additionalStyles}`;
    return PrintTemplate.div({ style }, children);
  }

  static renderElement(element: HtmlElement | string): string {
    if (typeof element === "string") {
      return element;
    }

    const { tag, attributes = {}, children = [], content = "" } = element;

    const attrs = Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(" ");

    const attrsStr = attrs ? ` ${attrs}` : "";

    if (children.length > 0) {
      const childrenStr = children
        .map((child) => PrintTemplate.renderElement(child))
        .join("");
      return `<${tag}${attrsStr}>${childrenStr}</${tag}>`;
    }

    return `<${tag}${attrsStr}>${content}</${tag}>`;
  }

  // Template builders for different print types
  static buildTableSessionHeader(data: CommonData): HtmlElement {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formattedDate = new Date(
      "start_time" in data.print_object && data.print_object.start_time
        ? data.print_object.start_time
        : "",
    ).toLocaleString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour12: false,
      timeZone: userTimezone,
    });

    const formattedHour = new Date(
      "start_time" in data.print_object && data.print_object.start_time
        ? data.print_object.start_time
        : "",
    ).toLocaleString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: userTimezone,
    });

    switch (data.print_type) {
      case "table_session": {
        return PrintTemplate.flexContainer(
          "column",
          "8px",
          "center",
          "center",
          "padding-bottom: 4px; border-bottom: 1px solid #000;",
          [
            PrintTemplate.span(
              `${data.restaurant_name ? `${data.restaurant_name}` : ""} `,
              "font--size-10 font--weight-500 font--ls-20",
            ),
            PrintTemplate.flexContainer(
              "column",
              "2px",
              "center",
              "center",
              "",
              [
                PrintTemplate.span(
                  "VIA DE FECHAMENTO",
                  "font--size-12 font--weight-700 font--ls-24",
                ),
                PrintTemplate.span(
                  `${formattedDate} - ${formattedHour}`,
                  "font--size-10 font--weight-500 font--ls-20 padding-top:4px",
                ),
              ],
            ),
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "center",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.span(
                  `${data.print_object.table.table_type === "table" ? "MESA" : "COMANDA"} ${data.print_object.table.table_number}`,
                  "font--size-12 font--weight-700 font--ls-24",
                ),
                PrintTemplate.span(
                  `Pedido #${data.print_object.id}`,
                  "font--size-12 font--weight-700 font--ls-24",
                ),
              ],
            ),
          ],
        );
      }
      case "order": {
        return PrintTemplate.div({}, [
          PrintTemplate.flexContainer(
            "column",
            "4px",
            "center",
            "center",
            "padding-bottom: 4px; border-bottom: 1px solid #000;",
            [
              PrintTemplate.span(
                `${data.restaurant_name ? `${data.restaurant_name}` : ""}`,
                "font--size-10 font--weight-500 font--ls-20",
              ),
              ...(data.order_type === "CANCELAMENTO"
                ? [
                    PrintTemplate.span(
                      "CANCELAMENTO",
                      "font--size-12 font--weight-700 font--ls-24",
                    ),
                  ]
                : []),
              PrintTemplate.flexContainer(
                "column",
                "2px",
                "center",
                "center",
                "",
                [
                  PrintTemplate.span(
                    `${formattedDate} - ${formattedHour}`,
                    "font--size-10 font--weight-500 font--ls-20",
                  ),
                ],
              ),
              PrintTemplate.flexContainer(
                "row",
                "0px",
                "center",
                "space-between",
                "width: 100%;",
                [
                  PrintTemplate.span(
                    `${data.print_object.table.table_type === "table" ? "MESA" : "COMANDA"} ${data.print_object.table.table_number}`,
                    "font--size-12 font--weight-700 font--ls-24",
                  ),
                  PrintTemplate.span(
                    `#${data.print_object.basket_id}`,
                    "font--size-12 font--weight-700 font--ls-24",
                  ),
                ],
              ),
            ],
          ),
          PrintTemplate.flexContainer(
            "column",
            "4px",
            "center",
            "center",
            "padding-bottom: 4px; border-bottom: 1px dashed #000; padding-top: 4px",
            [
              PrintTemplate.flexContainer(
                "row",
                "2px",
                "flex-start",
                "flex-start",
                "",
                [
                  ...("input_client" in data && data.input_client
                    ? [
                        PrintTemplate.span(
                          `Cliente: `,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.input_client}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ]
                    : [
                        PrintTemplate.span(
                          `Garçom: ${data.input_waiter}`,
                          "font--size-11 font--weight-700 font--ls-22",
                        ),
                      ]),
                ],
              ),
            ],
          ),
        ]);
      }
      case "individual_bill": {
        return PrintTemplate.flexContainer(
          "column",
          "8px",
          "center",
          "center",
          "padding-bottom: 4px; border-bottom: 1px solid #000;",
          [
            PrintTemplate.span(
              `${data.restaurant_name ? `${data.restaurant_name}` : ""} `,
              "font--size-10 font--weight-500 font--ls-20",
            ),
            PrintTemplate.flexContainer(
              "column",
              "2px",
              "center",
              "center",
              "",
              [
                PrintTemplate.span(
                  "COMANDA INDIVIDUAL",
                  "font--size-12 font--weight-700 font--ls-24",
                ),
                PrintTemplate.span(
                  `${formattedDate} - ${formattedHour}`,
                  "font--size-10 font--weight-500 font--ls-20 padding-top:4px",
                ),
              ],
            ),
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "center",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.span(
                  `${data.print_object.session.table.table_type === "table" ? "MESA" : "COMANDA"} ${data.print_object.session.table.table_number}`,
                  "font--size-12 font--weight-700 font--ls-24",
                ),
                PrintTemplate.span(
                  `Pedido #${data.print_object.id}`,
                  "font--size-12 font--weight-700 font--ls-24",
                ),
              ],
            ),
          ],
        );
      }
    }

    return PrintTemplate.div({});
  }

  static buildDeliveryHeader(data: CommonData): HtmlElement {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formattedDate = new Date(
      "start_time" in data.print_object && data.print_object.start_time
        ? data.print_object.start_time
        : "",
    ).toLocaleString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour12: false,
      timeZone: userTimezone,
    });

    const formattedHour = new Date(
      "start_time" in data.print_object && data.print_object.start_time
        ? data.print_object.start_time
        : "",
    ).toLocaleString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: userTimezone,
    });

    switch (data.print_type) {
      case "table_session": {
        const dateScheduled =
          "scheduled_to" in data.print_object && data.print_object.scheduled_to
            ? new Date(data.print_object.scheduled_to).toLocaleString("pt-BR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour12: false,
                timeZone: userTimezone,
              })
            : null;

        const hourScheduled =
          "scheduled_to" in data.print_object && data.print_object.scheduled_to
            ? new Date(data.print_object.scheduled_to).toLocaleString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
                timeZone: userTimezone,
              })
            : null;

        const withdrawalText =
          "with_withdrawal" in data.print_object &&
          data.print_object.with_withdrawal
            ? " RETIRADA"
            : "DELIVERY";
        const scheduled_to =
          "scheduled_to" in data.print_object &&
          data.print_object.scheduled_to &&
          data.print_object.scheduled_to.length > 0
            ? data.print_object.with_withdrawal
              ? "RETIRADA AGENDADA PARA"
              : "DELIVERY AGENDADO PARA"
            : null;
        const fechamento =
          "with_withdrawal" in data.print_object &&
          data.print_object.with_withdrawal
            ? "VIA DE FECHAMENTO - RETIRADA"
            : "VIA DE FECHAMENTO - DELIVERY";

        const ifood = data.print_object.ifood_discount;
        const neemo = data.print_object.bills[0].order_baskets[0].neemo_id;
        const entregaIfood =
          data.print_object.delivery_by &&
          data.print_object.delivery_by !== null
            ? data.print_object.delivery_by === "MERCHANT"
              ? "ENTREGA PRÓPRIA"
              : "ENTREGA IFOOD"
            : null;

        return PrintTemplate.div({}, [
          // Main header section
          PrintTemplate.flexContainer(
            "column",
            "8px",
            "center",
            "center",
            "padding-bottom: 4px; border-bottom: 1px solid #000;",
            [
              PrintTemplate.span(
                `${data.restaurant_name ? `${data.restaurant_name}` : ""} `,
                "font--size-10 font--weight-500 font--ls-20",
              ),

              PrintTemplate.flexContainer(
                "column",
                "0px",
                "center",
                "center",
                "",
                [
                  ...("status" in data.print_object &&
                  data.print_object.status === "completed"
                    ? [
                        PrintTemplate.span(
                          `${fechamento}`,
                          "font--size-12 font--weight-700 font--ls-24",
                        ),
                        PrintTemplate.span(
                          `${formattedDate} - ${formattedHour}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ]
                    : [
                        ...(scheduled_to
                          ? [
                              PrintTemplate.span(
                                `${scheduled_to}`,
                                "font--size-12 font--weight-700 font--ls-24",
                              ),
                              PrintTemplate.span(
                                `${dateScheduled} - ${hourScheduled}`,
                                "font--size-10 font--weight-500 font--ls-20",
                              ),
                            ]
                          : [
                              ifood
                                ? PrintTemplate.span(
                                    `IFOOD`,
                                    "font--size-12 font--weight-700 font--ls-24",
                                  )
                                : neemo
                                  ? PrintTemplate.span(
                                      `NEEMO`,
                                      "font--size-12 font--weight-700 font--ls-24",
                                    )
                                  : "",
                              PrintTemplate.span(
                                `${withdrawalText}`,
                                "font--size-12 font--weight-700 font--ls-24",
                              ),
                              PrintTemplate.span(
                                `${formattedDate} - ${formattedHour}`,
                                "font--size-10 font--weight-500 font--ls-20",
                              ),
                            ]),
                      ]),
                ],
              ),
              PrintTemplate.flexContainer(
                "row",
                "0px",
                "center",
                "center",
                "width: 100%;",
                [
                  ifood
                    ? PrintTemplate.flexContainer(
                        "row",
                        "0px",
                        "center",
                        "space-between",
                        "width: 100%;",
                        [
                          PrintTemplate.span(
                            `PEDIDO #${data.print_object.attendance_password}`,
                            "font--size-12 font--weight-700 font--ls-24",
                          ),
                          PrintTemplate.span(
                            `Ifood: ${String(data.order_number ? data.order_number : "-").replace(/^Nº\s*/i, "")}`,
                            "font--size-12 font--weight-700 font--ls-24",
                          ),
                        ],
                      )
                    : "with_withdrawal" in data.print_object &&
                        data.print_object.with_withdrawal === true
                      ? PrintTemplate.span(
                          `PEDIDO #${data.print_object.attendance_password || ""}`,
                          "font--size-12 font--weight-700 font--ls-24",
                        )
                      : PrintTemplate.flexContainer(
                          "row",
                          "0px",
                          "center",
                          "center",
                          "width: 100%;",
                          [
                            PrintTemplate.span(
                              "attendance_password" in data.print_object
                                ? `PEDIDO #${data.print_object.attendance_password || ""}`
                                : "",
                              "font--size-12 font--weight-700 font--ls-24",
                            ),
                          ],
                        ),
                ],
              ),
            ],
          ),
          ...(data.print_object.ifood_discount
            ? [
                PrintTemplate.flexContainer(
                  "column",
                  "4px",
                  "center",
                  "center",
                  "width: 100%; padding-top: 4px;",
                  [
                    ...(entregaIfood
                      ? [
                          PrintTemplate.span(
                            `${entregaIfood}`,
                            "font--size-10 font--weight-700 font--ls-20",
                          ),
                        ]
                      : [
                          PrintTemplate.span(
                            ``,
                            "font--size-10 font--weight-700 font--ls-20",
                          ),
                        ]),
                  ],
                ),
                PrintTemplate.flexContainer(
                  "column",
                  "2px",
                  "flex-start",
                  "flex-start",
                  "padding-top: 4px; padding-bottom: 4px; border-bottom: 1px dashed #000;",
                  [
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Data e hora:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${formattedDate} - ${formattedHour}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Localizador:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${"buyer" in data.print_object.bills[0] && data.print_object.bills[0].buyer?.localizer}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                  ],
                ),
              ]
            : []),

          PrintTemplate.flexContainer(
            "column",
            "2px",
            "flex-start",
            "flex-start",
            "padding-top: 4px; padding-bottom: 4px; border-bottom: 1px dashed #000;",
            [
              PrintTemplate.flexContainer(
                "row",
                "2px",
                "flex-start",
                "flex-start",
                "",
                [
                  PrintTemplate.span(
                    `Cliente: `,
                    "font--size-10 font--weight-700 font--ls-20",
                  ),
                  PrintTemplate.span(
                    "bills" in data.print_object &&
                      data.print_object.bills[0]?.buyer?.name
                      ? `${data.print_object.bills[0].buyer.name}`
                      : "-",
                    "font--size-10 font--weight-500 font--ls-20",
                  ),
                ],
              ),
              PrintTemplate.flexContainer(
                "row",
                "2px",
                "flex-start",
                "flex-start",
                "",
                [
                  PrintTemplate.span(
                    `Tel: `,
                    "font--size-10 font--weight-700 font--ls-20",
                  ),
                  PrintTemplate.span(
                    "bills" in data.print_object &&
                      data.print_object.bills[0]?.buyer?.phone
                      ? `${data.print_object.bills[0].buyer.phone}`
                      : "-",
                    "font--size-10 font--weight-500 font--ls-20",
                  ),
                ],
              ),

              ...("is_delivery" in data.print_object &&
              "with_withdrawal" in data.print_object &&
              data.print_object.is_delivery &&
              !data.print_object.with_withdrawal
                ? [
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Endereço:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.street || "-"}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Número:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.number || "-"}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Comp:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.complement || "-"}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Bairro:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.neighborhood || "-"}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Ref:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.reference || "-"}`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Cidade:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.city || "-"} -`,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),

                        PrintTemplate.span(
                          `CEP:`,
                          "font--size-10 font--weight-700 font--ls-20",
                        ),
                        PrintTemplate.span(
                          `${data.print_object.buyer_address?.zip_code || "-"} `,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    ...(data.print_object.details &&
                    data.print_object.details?.length > 0
                      ? [
                          PrintTemplate.flexContainer(
                            "row",
                            "4px",
                            "flex-start",
                            "flex-start",
                            "width: 100%; padding-top: 2px; ",
                            [
                              PrintTemplate.span(
                                `Info Adicional:`,
                                "font--size-10 font--weight-700 font--ls-20",
                              ),
                              PrintTemplate.span(
                                `${data.print_object.details} `,
                                "font--size-10 font--weight-500 font--ls-20",
                              ),
                            ],
                          ),
                        ]
                      : []),
                    ...(data.print_object.motoboy
                      ? [
                          PrintTemplate.flexContainer(
                            "column",
                            "4px",
                            "center",
                            "center",
                            "width: 100%; padding-top: 4px; border-top: 1px dashed #000;",
                            [
                              PrintTemplate.span(
                                `ENTREGA PRÓPRIA:`,
                                "font--size-10 font--weight-700 font--ls-20",
                              ),
                              PrintTemplate.span(
                                `Motoboy ${data.print_object.motoboy?.name}`,
                                "font--size-10 font--weight-500 font--ls-20",
                              ),
                            ],
                          ),
                        ]
                      : []),
                  ]
                : []),
            ],
          ),
        ]);
      }
      case "order": {
        const scheduled_To =
          "scheduled_to" in data &&
          data.scheduled_to &&
          data.scheduled_to.length > 0
            ? "with_withdrawal" in data &&
              data.with_withdrawal === "Retirada Agendado"
              ? "RETIRADA AGENDADA PARA"
              : "DELIVERY AGENDADO PARA"
            : null;
        const deliveryIfood =
          "is_delivery" in data &&
          "with_withdrawal" in data &&
          data.is_delivery &&
          data.with_withdrawal === "iFood";

        const ifood =
          data.with_withdrawal === "iFood - Delivery" ||
          data.with_withdrawal === "iFood - Retirada";

        const dateScheduled =
          "scheduled_to" in data && data.scheduled_to
            ? new Date(data.scheduled_to).toLocaleString("pt-BR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour12: false,
                timeZone: userTimezone,
              })
            : null;

        const hourScheduled =
          "scheduled_to" in data && data.scheduled_to
            ? new Date(data.scheduled_to).toLocaleString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
                timeZone: userTimezone,
              })
            : null;

        return PrintTemplate.div({}, [
          PrintTemplate.flexContainer(
            "column",
            "8px",
            "center",
            "center",
            "padding-bottom: 4px; border-bottom: 1px solid #000;",
            [
              ...(scheduled_To
                ? [
                    ifood
                      ? PrintTemplate.span(
                          `IFOOD`,
                          "font--size-12 font--weight-700 font--ls-24",
                        )
                      : "",
                    PrintTemplate.span(
                      `${scheduled_To}`,
                      "font--size-12 font--weight-700 font--ls-24",
                    ),
                  ]
                : deliveryIfood
                  ? [
                      PrintTemplate.span(
                        `Delivery IFood`,
                        "font--size-12 font--weight-700 font--ls-24",
                      ),
                    ]
                  : "with_withdrawal" in data &&
                      data.with_withdrawal === "Retirada"
                    ? [
                        PrintTemplate.span(
                          `Retirada`,
                          "font--size-12 font--weight-700 font--ls-24",
                        ),
                      ]
                    : [
                        PrintTemplate.span(
                          `Delivery`,
                          "font--size-12 font--weight-700 font--ls-24",
                        ),
                      ]),

              scheduled_To
                ? PrintTemplate.span(
                    `${dateScheduled} - ${hourScheduled}`,
                    "font--size-10 font--weight-500 font--ls-20",
                  )
                : PrintTemplate.span(
                    `${formattedDate} - ${formattedHour}`,
                    "font--size-10 font--weight-500 font--ls-20",
                  ),

              deliveryIfood || ifood
                ? PrintTemplate.flexContainer(
                    "row",
                    "0px",
                    "center",
                    "space-between",
                    "width: 100%;",
                    [
                      PrintTemplate.span(
                        `PEDIDO #${data.print_object.basket_id}`,
                        "font--size-12 font--weight-700 font--ls-24",
                      ),
                      PrintTemplate.span(
                        `Ifood: ${String(data.print_object.order_number).replace(/^Nº\s*/i, "")}`,
                        "font--size-12 font--weight-700 font--ls-24",
                      ),
                    ],
                  )
                : PrintTemplate.flexContainer(
                    "row",
                    "0px",
                    "center",
                    "space-between",
                    "width: 100%;",
                    [
                      PrintTemplate.span(
                        `NOVO PEDIDO`,
                        "font--size-12 font--weight-700 font--ls-20",
                      ),
                      PrintTemplate.span(
                        `#${"basket_id" in data.print_object && data.print_object.basket_id}`,
                        "font--size-12 font--weight-700 font--ls-20",
                      ),
                    ],
                  ),
            ],
          ),
          PrintTemplate.flexContainer(
            "column",
            "4px",
            "row",
            "center",
            "padding-bottom: 4px; border-bottom: 1px dashed #000; padding-top: 4px",
            [
              ...(deliveryIfood || ifood
                ? [
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Cliente: `,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                        PrintTemplate.span(
                          "bill" in data.print_object &&
                            data.print_object.bill?.buyer?.name
                            ? `${data.print_object.bill.buyer.name}`
                            : "-",
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                  ]
                : [
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          `Cliente: `,
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                        PrintTemplate.span(
                          "bill" in data.print_object &&
                            data.print_object.bill?.buyer?.name
                            ? `${data.print_object.bill.buyer.name}`
                            : "-",
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                    PrintTemplate.flexContainer(
                      "row",
                      "2px",
                      "flex-start",
                      "flex-start",
                      "",
                      [
                        PrintTemplate.span(
                          "bill" in data.print_object &&
                            data.print_object.bill?.buyer?.phone
                            ? `${data.print_object.bill.buyer.phone}`
                            : "-",
                          "font--size-10 font--weight-500 font--ls-20",
                        ),
                      ],
                    ),
                  ]),
            ],
          ),
        ]);
      }
    }
    return PrintTemplate.div({});
  }

  static buildOrderItem(data: Order, showPrice: boolean = true) {
    const allComplements = data.complement_categories.flatMap((category) => {
      return category.order_complements.map((comp) => {
        return {
          name: comp.complement.name,
          amount: comp.amount,
        };
      });
    });
    return PrintTemplate.flexContainer(
      "column",
      "0px",
      "flex-start",
      "space-around",
      "width: 100%;",
      [
        ...(showPrice
          ? [
              PrintTemplate.flexContainer(
                "row",
                "0px",
                "flex-start",
                "space-between",
                "width: 100%;",
                [
                  PrintTemplate.div(
                    { style: "display: flex; align-items: center; gap: 8px" },
                    [
                      PrintTemplate.span(
                        `${data.amount}x`,
                        "font--size-10 font--weight-500 font--ls-20",
                        { style: "width: 17px;" },
                      ),
                      PrintTemplate.span(
                        `${data.product.name}`,
                        "font--size-10 font--weight-500 font--ls-20",
                      ),
                    ],
                  ),
                  PrintTemplate.span(
                    `R$ ${data.total_price.replace(".", ",")}`,
                    "font--size-10 nowrap font--weight-500",
                  ),
                ],
              ),
              PrintTemplate.flexContainer(
                "column",
                "0px",
                "flex-start",
                "flex-start",
                "margin-left: 17px; width: calc(100% - 17px);",
                [
                  ...(allComplements.length > 0
                    ? [
                        PrintTemplate.flexContainer(
                          "column",
                          "0px",
                          "flex-start",
                          "flex-start",
                          "",
                          [
                            ...allComplements.map((complement) =>
                              PrintTemplate.flexContainer(
                                "row",
                                "2px",
                                "center",
                                "center",
                                "",
                                [
                                  PrintTemplate.span(
                                    `-`,
                                    "font--size-9 font--weight-500 font--ls-18",
                                    { style: "width: 6px;" },
                                  ),
                                  PrintTemplate.span(
                                    `${complement.amount}x`,
                                    "font--size-9 font--weight-500 font--ls-18",
                                  ),
                                  PrintTemplate.span(
                                    `${complement.name}`,
                                    "font--size-9 font--weight-500 font--ls-18",
                                    { style: "margin-left: 4px;" },
                                  ),
                                ],
                              ),
                            ),
                          ],
                        ),
                      ]
                    : []),
                  ...(data.details
                    ? [
                        PrintTemplate.flexContainer(
                          "row",
                          "0px",
                          "flex-start",
                          "space-between",
                          "width: 100%; padding: 2px 4px; border-radius: 2px; background: #000; width: 100%;",
                          [
                            PrintTemplate.span(
                              `OBS: ${data.details}`,
                              "font--size-9 font--weight-600 font--ls-18",
                              {
                                style:
                                  "color: #fff; width: 100%; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;",
                              },
                            ),
                          ],
                        ),
                      ]
                    : []),
                ],
              ),
            ]
          : [
              PrintTemplate.flexContainer(
                "row",
                "0px",
                "flex-start",
                "space-between",
                "width: 100%;",
                [
                  PrintTemplate.div(
                    { style: "display: flex; align-items: center; gap: 8px" },
                    [
                      PrintTemplate.span(
                        `${data.amount}x`,
                        "font--size-11 font--weight-500 font--ls-20",
                        { style: "width: 17px;" },
                      ),
                      PrintTemplate.span(
                        `${data.product.name}`,
                        "font--size-11 font--weight-500 font--ls-20",
                      ),
                    ],
                  ),
                ],
              ),
              PrintTemplate.flexContainer(
                "column",
                "0px",
                "flex-start",
                "flex-start",
                "margin-left: 17px; width: calc(100% - 17px);",
                [
                  ...(allComplements.length > 0
                    ? [
                        PrintTemplate.flexContainer(
                          "column",
                          "0px",
                          "flex-start",
                          "flex-start",
                          "",
                          [
                            ...allComplements.map((complement) =>
                              PrintTemplate.flexContainer(
                                "row",
                                "2px",
                                "center",
                                "center",
                                "",
                                [
                                  PrintTemplate.span(
                                    `-`,
                                    "font--size-11 font--weight-500 font--ls-18",
                                    { style: "width: 6px;" },
                                  ),
                                  PrintTemplate.span(
                                    `${complement.amount}x`,
                                    "font--size-11 font--weight-500 font--ls-18",
                                  ),
                                  PrintTemplate.span(
                                    `${complement.name}`,
                                    "font--size-11 font--weight-500 font--ls-18",
                                    { style: "margin-left: 4px;" },
                                  ),
                                ],
                              ),
                            ),
                          ],
                        ),
                      ]
                    : []),
                  ...(data.details
                    ? [
                        PrintTemplate.flexContainer(
                          "row",
                          "0px",
                          "flex-start",
                          "space-between",
                          "width: 100%; padding: 2px 4px; border-radius: 2px; background: #000; width: 100%;",
                          [
                            PrintTemplate.span(
                              `OBS: ${data.details}`,
                              "font--size-12 font--weight-600 font--ls-18",
                              {
                                style:
                                  "color: #fff; width: 100%; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;",
                              },
                            ),
                          ],
                        ),
                      ]
                    : []),
                ],
              ),
            ]),
      ],
    );
  }

  static buildOrders(data: Bill, txService: number) {
    const allOrders = data.order_baskets.flatMap((basket) => {
      return basket.orders.map((order) => {
        return PrintTemplate.buildOrderItem(order);
      });
    });

    return PrintTemplate.flexContainer(
      "column",
      "4px",
      "center",
      "center",
      "width: 100%; padding-top: 4px; padding-bottom: 6px; border-bottom: 1px dashed #000;",
      [
        ...(data.waiter
          ? [
              PrintTemplate.span(
                "Garçom",
                "font--size-11 font--weight-700 font--ls-22",
              ),
            ]
          : [
              PrintTemplate.span(
                `Cliente: ${data.buyer?.name || "Itens do pedido"}`,
                "font--size-11 font--weight-700 font--ls-22",
              ),
            ]),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "flex-start",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.div(
                  { style: "display: flex; align-items: center; gap: 8px" },
                  [
                    PrintTemplate.span(
                      "QTD",
                      "font--size-9 font--weight-700 font--ls-18",
                      { style: "width: 17px;" },
                    ),
                    PrintTemplate.span(
                      "ITENS",
                      "font--size-9 font--weight-700 font--ls-18",
                    ),
                  ],
                ),
                PrintTemplate.span(
                  "TOTAL",
                  "font--size-9 font--weight-700 font--ls-18",
                  { style: "text-align: right; width: 28px;" },
                ),
              ],
            ),
            ...(allOrders.length > 0 ? allOrders : []),
          ],
        ),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "center",
              "space-between",
              "width: 100%;",
              [
                ...(txService
                  ? [
                      PrintTemplate.span(
                        `Tx. de Serviço: (${txService ? txService : 0}% - opcional)`,
                        "font--size-9 font--weight-500 font--ls-18",
                      ),
                      PrintTemplate.span(
                        `R$ ${(Number(data.total_service_price) - Number(data.total_price)).toFixed(2).replace(".", ",")}`,
                        "font--size-9 font--weight-500 font--ls-18",
                      ),
                    ]
                  : []),
              ],
            ),
            PrintTemplate.span(
              `TOTAL: R$ ${data.total_service_price.replace(".", ",")}`,
              "font--size-10 nowrap font--weight-700 font--ls-20",
              { style: "text-align: right; width: 100%;" },
            ),
          ],
        ),
      ],
    );
  }

  static buildOrdersIndividual(data: CommonData) {
    const allOrders =
      "order_baskets" in data.print_object &&
      data.print_object.order_baskets.flatMap((basket) => {
        return basket.orders.map((order) => {
          return PrintTemplate.buildOrderItem(order);
        });
      });

    const taxService = parseInt(data.service_tax, 10);
    const serviceTaxPrice = data.service_tax;

    return PrintTemplate.flexContainer(
      "column",
      "4px",
      "center",
      "center",
      "width: 100%; padding-top: 4px; padding-bottom: 6px; border-bottom: 1px dashed #000;",
      [
        ...("waiter" in data.print_object && data.print_object.waiter
          ? [
              PrintTemplate.span(
                "Garçom",
                "font--size-11 font--weight-700 font--ls-22",
              ),
            ]
          : [
              PrintTemplate.span(
                `Cliente: ${("buyer" in data.print_object && data.print_object.buyer?.name) || "Itens do pedido"}`,
                "font--size-11 font--weight-700 font--ls-22",
              ),
            ]),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "flex-start",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.div(
                  { style: "display: flex; align-items: center; gap: 8px" },
                  [
                    PrintTemplate.span(
                      "QTD",
                      "font--size-9 font--weight-700 font--ls-18",
                      { style: "width: 17px;" },
                    ),
                    PrintTemplate.span(
                      "ITENS",
                      "font--size-9 font--weight-700 font--ls-18",
                    ),
                  ],
                ),
                PrintTemplate.span(
                  "TOTAL",
                  "font--size-9 font--weight-700 font--ls-18",
                  { style: "text-align: right; width: 28px;" },
                ),
              ],
            ),
            ...(allOrders && allOrders.length > 0 ? allOrders : []),
          ],
        ),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "center",
              "space-between",
              "width: 100%;",
              [
                ...(serviceTaxPrice &&
                "service_tax" in data.print_object &&
                data.print_object.service_tax
                  ? [
                      PrintTemplate.span(
                        `Tx. de Serviço: (${taxService ? taxService : 0}% - opcional)`,
                        "font--size-9 font--weight-500 font--ls-18",
                      ),
                      PrintTemplate.span(
                        `R$ ${(Number(data.print_object.session.total_service_price) - Number(data.print_object.session.total_price)).toFixed(2).replace(".", ",")}`,
                        "font--size-9 font--weight-500 font--ls-18",
                      ),
                    ]
                  : []),
              ],
            ),
            PrintTemplate.span(
              `TOTAL: R$ ${data.print_object.total_service_price.replace(".", ",")}`,
              "font--size-10 font--weight-700 font--ls-20",
              { style: "text-align: right; width: 100%;" },
            ),
          ],
        ),
      ],
    );
  }

  static buildOrdersDelivery(data: Bill) {
    const allOrders = data.order_baskets.flatMap((basket) => {
      return basket.orders.map((order) => {
        return PrintTemplate.buildOrderItem(order);
      });
    });
    const valueTotal = data.order_baskets
      .flatMap((basket) => basket.orders)
      .reduce((soma, order) => soma + parseFloat(order.total_service_price), 0); //o total em delivery fazemos um reduce pois em pedidos de ifood o total_service_price n vem com o valor correto.. vem com alguns cupons aplicados

    return PrintTemplate.flexContainer(
      "column",
      "4px",
      "center",
      "center",
      "width: 100%; padding-top: 4px; padding-bottom: 6px; border-bottom: 1px dashed #000;",
      [
        PrintTemplate.span(
          "Itens do pedido",
          "font--size-11 font--weight-700 font--ls-22",
        ),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "flex-start",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.div(
                  { style: "display: flex; align-items: center; gap: 8px" },
                  [
                    PrintTemplate.span(
                      "QTD",
                      "font--size-9 font--weight-700 font--ls-18",
                      { style: "width: 17px;" },
                    ),
                    PrintTemplate.span(
                      "ITENS",
                      "font--size-9 font--weight-700 font--ls-18",
                    ),
                  ],
                ),
                PrintTemplate.span(
                  "TOTAL",
                  "font--size-9 font--weight-700 font--ls-18",
                  { style: "text-align: right; width: 28px;" },
                ),
              ],
            ),
            ...(allOrders.length > 0 ? allOrders.flat() : []),
          ],
        ),

        PrintTemplate.span(
          `TOTAL: R$ ${Number(valueTotal).toFixed(2).replace(".", ",")}`,
          "font--size-10 nowrap font--weight-700 font--ls-20",
          { style: "text-align: right; width: 100%;" },
        ),
      ],
    );
  }

  static buildOrdersProduction(data: CommonData) {
    // a diferença do buildOrdersDelivery e buildOrders é que nesse é para via de produção delivery e pdv tirando o total tx.de Serviço e ele recebe commonData
    const allOrders =
      "orders" in data.print_object
        ? data.print_object.orders.flatMap((basket) => {
            return PrintTemplate.buildOrderItem(basket, false);
          })
        : [];
    return PrintTemplate.flexContainer(
      "column",
      "4px",
      "center",
      "center",
      "width: 100%; padding-top: 4px; padding-bottom: 6px; border-bottom: 1px dashed #000;",
      [
        PrintTemplate.span(
          "Itens do pedido",
          "font--size-11 font--weight-700 font--ls-22",
        ),
        PrintTemplate.flexContainer(
          "column",
          "2px",
          "flex-start",
          "flex-start",
          "width: 100%;",
          [
            PrintTemplate.flexContainer(
              "row",
              "0px",
              "flex-start",
              "space-between",
              "width: 100%;",
              [
                PrintTemplate.div(
                  { style: "display: flex; align-items: center; gap: 8px" },
                  [
                    PrintTemplate.span(
                      "QTD",
                      "font--size-9 font--weight-700 font--ls-18",
                      { style: "width: 17px;" },
                    ),
                    PrintTemplate.span(
                      "ITENS",
                      "font--size-9 font--weight-700 font--ls-18",
                    ),
                  ],
                ),
              ],
            ),
            ...(allOrders.length > 0 ? allOrders.flat() : []),
          ],
        ),
      ],
    );
  }

  static buildAllOrders(data: CommonData): HtmlElement {
    const taxService = parseInt(data.service_tax, 10);
    switch (data.print_type) {
      case "table_session": {
        const waiterBills =
          "bills" in data.print_object
            ? data.print_object.bills.filter(
                (bill) => bill.waiter && bill.waiter !== null,
              )
            : [];
        const groupedWaiterBills =
          waiterBills.length > 0
            ? [
                {
                  ...waiterBills[0],
                  order_baskets: waiterBills.flatMap(
                    (bill) => bill.order_baskets,
                  ),
                  total_price: waiterBills
                    .reduce((sum, bill) => sum + Number(bill.total_price), 0)
                    .toFixed(2),
                  total_service_price: waiterBills
                    .reduce(
                      (sum, bill) => sum + Number(bill.total_service_price),
                      0,
                    )
                    .toFixed(2),
                },
              ]
            : [];
        const filterBuyerBills =
          "bills" in data.print_object
            ? data.print_object.bills.filter(
                (bill) => !bill.waiter || bill.waiter === null,
              )
            : [];
        return PrintTemplate.flexContainer(
          "column",
          "0px",
          "flex-start",
          "flex-start",
          "style: width: 100%;",
          [
            ...groupedWaiterBills.map((bill) =>
              PrintTemplate.buildOrders(bill, taxService),
            ),
            ...filterBuyerBills.map((bill) =>
              PrintTemplate.buildOrders(bill, taxService),
            ),
          ],
        );
      }

      case "order": {
        return PrintTemplate.flexContainer(
          "column",
          "0px",
          "flex-start",
          "flex-start",
          "style: width: 100%;",
          [
            ...("bill" in data.print_object
              ? [PrintTemplate.buildOrdersProduction(data)]
              : []),
          ],
        );
      }
      case "individual_bill": {
        return PrintTemplate.flexContainer(
          "column",
          "0px",
          "flex-start",
          "flex-start",
          "style: width: 100%;",
          [PrintTemplate.buildOrdersIndividual(data)],
        );
      }
    }
    return PrintTemplate.div({});
  }

  static buildAllOrdersDelivery(data: CommonData): HtmlElement {
    switch (data.print_type) {
      case "table_session": {
        return PrintTemplate.flexContainer(
          "column",
          "0px",
          "flex-start",
          "flex-start",
          "style: width: 100%;",
          [
            ...("bills" in data.print_object
              ? data.print_object.bills.map((bill) =>
                  PrintTemplate.buildOrdersDelivery(bill),
                )
              : []),
          ],
        );
      }
      case "order": {
        return PrintTemplate.flexContainer(
          "column",
          "0px",
          "flex-start",
          "flex-start",
          "style: width: 100%;",
          [
            ...("bill" in data.print_object
              ? [PrintTemplate.buildOrdersProduction(data)]
              : []),
          ],
        );
      }
    }
    return PrintTemplate.div({});
  }

  // static buildOrderItems(data: TableSessionPrintPayload): HtmlElement {
  //   // Extract order items from bills -> order_baskets -> orders
  //   const orderItems = data.print_object.bills.flatMap(bill =>
  //     bill.order_baskets?.flatMap(basket => basket.orders || []) || []
  //   )

  //   return this.div({ style: 'padding: 8px 0;' }, [
  //     this.span('Itens do Pedido:', 'font--size-11 font--weight-600'),
  //     ...orderItems.map(item =>
  //       this.flexContainer('row', '0px', 'center', 'space-between', 'margin-top: 4px;', [
  //         this.span(`${item.amount}x ${item.product?.name || 'Produto'}`, 'font--size-10'),
  //         this.span(`R$ ${item.total_price}`, 'font--size-10 font--weight-600')
  //       ])
  //     )
  //   ])
  // }

  static buildTotals(data: CommonData): HtmlElement {
    const taxService = parseInt(data.service_tax, 10);
    const serviceTaxPrice = data.service_tax;

    switch (data.print_type) {
      case "table_session": {
        return ("status" in data.print_object &&
          data.print_object.status === "completed") ||
          ("is_delivery" in data.print_object &&
            data.print_type === "table_session" &&
            data.print_object.is_delivery === false)
          ? PrintTemplate.flexContainer(
              "column",
              "4px",
              "center",
              "center",
              "margin-top: 4px; padding-bottom: 4px; border-bottom: 1px dashed #000;",
              [
                PrintTemplate.span(
                  "Consumo Total",
                  "font--size-11 font--weight-700 font--ls-22",
                  { style: "width: 100%; text-align: center;" },
                ),
                PrintTemplate.flexContainer(
                  "column",
                  "2px",
                  "center",
                  "center",
                  "width: 100%;",
                  [
                    PrintTemplate.flexContainer(
                      "row",
                      "0px",
                      "flex-start",
                      "space-between",
                      "width: 100%;",
                      [
                        PrintTemplate.span(
                          "Valor dos Itens",
                          "font--size-9 font--weight-500 font--ls-18",
                        ),
                        ...(data.print_object.old_total_price &&
                        data.print_object.old_total_price.length > 0
                          ? [
                              PrintTemplate.span(
                                `R$ ${data.print_object.old_total_price.replace(".", ",")}`,
                                "font--size-9 nowrap font--weight-500 font--ls-18",
                              ),
                            ]
                          : [
                              PrintTemplate.span(
                                `R$ ${data.print_object.total_price.replace(".", ",")}`,
                                "font--size-9 nowrap font--weight-500 font--ls-18",
                              ),
                            ]),
                      ],
                    ),

                    ...(data.print_object.discount_total &&
                    data.print_object.discount_total?.length > 0
                      ? [
                          PrintTemplate.flexContainer(
                            "row",
                            "0px",
                            "flex-start",
                            "space-between",
                            "width: 100%;",
                            [
                              PrintTemplate.span(
                                `Cupom/Desconto`,
                                "font--size-9 font--weight-500 font--ls-18",
                              ),
                              PrintTemplate.span(
                                `R$ -${data.print_object.discount_total.replace(".", ",")}`,
                                "font--size-9 font--weight-500 font--ls-18",
                              ),
                            ],
                          ),
                        ]
                      : []),
                    ...(data.print_object.is_delivery
                      ? [
                          ...(data.print_object.with_withdrawal === false
                            ? [
                                PrintTemplate.flexContainer(
                                  "row",
                                  "0px",
                                  "flex-start",
                                  "space-between",
                                  "width: 100%;",
                                  [
                                    PrintTemplate.span(
                                      "Taxa de Entrega",
                                      "font--size-9 font--weight-500 font--ls-18",
                                    ),
                                    PrintTemplate.span(
                                      `R$ ${data.print_object.delivery_tax_price}`,
                                      "font--size-9 font--weight-500 font--ls-18",
                                    ),
                                  ],
                                ),
                              ]
                            : []),
                          PrintTemplate.flexContainer(
                            "row",
                            "0px",
                            "flex-start",
                            "space-between",
                            "width: 100%;",
                            [
                              PrintTemplate.span(
                                "TOTAL A PAGAR",
                                "font--size-10 font--weight-700 font--ls-20",
                              ),
                              PrintTemplate.span(
                                `R$ ${String(data.print_object.total_delivery_price)?.replace(".", ",")}`,
                                "font--size-10 nowrap font--weight-700 font--ls-20",
                                { style: "text-align: right;" },
                              ),
                            ],
                          ),
                        ]
                      : [
                          PrintTemplate.flexContainer(
                            "row",
                            "0px",
                            "flex-start",
                            "space-between",
                            "width: 100%;",
                            [
                              ...(serviceTaxPrice &&
                              Array.isArray(data.print_object.bills) &&
                              data.print_object.bills.some(
                                (bill) => bill.service_tax,
                              )
                                ? [
                                    PrintTemplate.span(
                                      `Tx. de Serviço: (${taxService ? taxService : 0}% - opcional)`,
                                      "font--size-9 font--weight-500 font--ls-18",
                                    ),
                                    PrintTemplate.span(
                                      `R$ ${(Number(data.print_object.total_service_price) - Number(data.print_object.total_price)).toFixed(2).replace(".", ",")}`,
                                      "font--size-9 font--weight-500 font--ls-18",
                                    ),
                                  ]
                                : []),
                            ],
                          ),
                          PrintTemplate.flexContainer(
                            "row",
                            "0px",
                            "flex-start",
                            "space-between",
                            "width: 100%;",
                            [
                              PrintTemplate.span(
                                "TOTAL A PAGAR",
                                "font--size-10 font--weight-700 font--ls-20",
                              ),
                              PrintTemplate.span(
                                `R$ ${data.print_object.total_service_price.replace(".", ",")}`,
                                "font--size-10 nowrap font--weight-700 font--ls-20",
                                { style: "text-align: right;" },
                              ),
                            ],
                          ),
                        ]),
                  ],
                ),
              ],
            )
          : PrintTemplate.div({}, []);
      }
      case "order": {
        return PrintTemplate.div({});
      }
      case "individual_bill": {
        return PrintTemplate.div({});
      }
    }
    return PrintTemplate.div({});
  }

  static buildPayments(data: CommonData): HtmlElement {
    switch (data.print_type) {
      case "table_session": {
        const totalPaid =
          "payments" in data.print_object
            ? data.print_object.payments.reduce(
                (sum, payment) => sum + Number(payment.payment_value),
                0,
              )
            : 0;
        const remainingValue =
          Number(data.print_object.total_service_price) - Number(totalPaid);
        const delivery =
          Number(
            "total_delivery_price" in data.print_object &&
              data.print_object.total_delivery_price,
          ) - Number(totalPaid);

        return PrintTemplate.flexContainer(
          "column",
          "4px",
          "center",
          "center",
          "margin-top: 4px; padding-bottom: 4px; border-bottom: 1px solid #000;",
          [
            PrintTemplate.span(
              "Formas de Pagamento",
              "font--size-11 font--weight-700 font--ls-22",
              { style: "width: 100%; text-align: center;" },
            ),
            PrintTemplate.flexContainer(
              "column",
              "2px",
              "center",
              "center",
              "width: 100%; padding-bottom: 2px; border-bottom: 1px dashed #000;",
              [
                PrintTemplate.flexContainer(
                  "row",
                  "0px",
                  "flex-start",
                  "space-between",
                  "width: 100%;",
                  [
                    PrintTemplate.span(
                      "VALOR PAGO",
                      "font--size-10 font--weight-700 font--ls-20",
                    ),
                    PrintTemplate.span(
                      `R$ ${totalPaid.toFixed(2).replace(".", ",")}`,
                      "font--size-10 font--weight-700 font--ls-20",
                    ),
                  ],
                ),
                ...("payments" in data.print_object &&
                data.print_object.payments.length > 0
                  ? data.print_object.payments.map((payment) =>
                      PrintTemplate.flexContainer(
                        "row",
                        "0px",
                        "flex-start",
                        "space-between",
                        "width: 100%;",
                        [
                          PrintTemplate.span(
                            `${payment.payment_method.name}`,
                            "font--size-9 font--weight-500 font--ls-18",
                          ),
                          PrintTemplate.span(
                            `R$ ${payment.payment_value.replace(".", ",")}`,
                            "font--size-9 font--weight-500 font--ls-18",
                          ),
                        ],
                      ),
                    )
                  : []),
              ],
            ),
            ...(remainingValue
              ? [
                  PrintTemplate.flexContainer(
                    "row",
                    "0px",
                    "flex-start",
                    "space-between",
                    "width: 100%;",
                    [
                      ...("is_delivery" in data.print_object &&
                      data.print_object.is_delivery
                        ? [
                            PrintTemplate.span(
                              `${delivery < 0 ? "TROCO" : "VALOR RESTANTE"}`,
                              "font--size-10 font--weight-700 font--ls-20",
                            ),
                            PrintTemplate.span(
                              `R$ ${delivery < 0 ? (-1 * delivery).toFixed(2).replace(".", ",") : delivery.toFixed(2).replace(".", ",")}`,
                              "font--size-10 font--weight-700 font--ls-20",
                            ),
                          ]
                        : [
                            PrintTemplate.span(
                              `${remainingValue < 0 ? "TROCO" : "VALOR RESTANTE"}`,
                              "font--size-10 font--weight-700 font--ls-20",
                            ),
                            PrintTemplate.span(
                              `R$ ${remainingValue < 0 ? (-1 * remainingValue).toFixed(2).replace(".", ",") : remainingValue.toFixed(2).replace(".", ",")}`,
                              "font--size-10 font--weight-700 font--ls-20",
                            ),
                          ]),
                    ],
                  ),
                ]
              : []),
          ],
        );
      }

      case "order": {
        return PrintTemplate.div({});
      }
    }

    return PrintTemplate.div({});
  }

  static buildPaymentsDelivery(data: CommonData): HtmlElement {
    const valueDelivery =
      "total_paid" in data.print_object &&
      "total_delivery_price" in data.print_object &&
      Number(data.print_object.total_paid) -
        Number(data.print_object.total_delivery_price) ===
        0;
    const cupomIfood =
      "ifood_discount" in data.print_object && data.print_object.ifood_discount
        ? parseFloat(data.print_object.ifood_discount)
        : 0;
    const taxServiceIfood =
      "additional_fees" in data.print_object &&
      data.print_object.additional_fees
        ? parseFloat(data.print_object.additional_fees)
        : 0;
    const valueTotal =
      "bills" in data.print_object &&
      data.print_object.bills
        .flatMap((bill) => bill.order_baskets)
        .flatMap((basket) => basket.orders)
        .reduce(
          (soma, order) => soma + parseFloat(order.total_service_price),
          0,
        );
    const totalPaidIfood =
      "ifood_discount" in data.print_object &&
      !Number.isNaN(Number(data.print_object.ifood_discount)) &&
      !Number.isNaN(Number(data.print_object.total_paid))
        ? Math.abs(
            Number(data.print_object.ifood_discount) -
              Number(data.print_object.total_paid),
          ).toFixed(2)
        : "0.00";

    switch (data.print_type) {
      case "table_session": {
        return PrintTemplate.flexContainer(
          "column",
          "4px",
          "center",
          "center",
          "margin-top: 4px; padding-bottom: 4px;",
          [
            ...("status" in data.print_object &&
            data.print_object.status === "completed"
              ? [
                  PrintTemplate.flexContainer(
                    "row",
                    "2px",
                    "flex-start",
                    "space-between",
                    "width: 100%; display: block;",
                    [PrintTemplate.buildPayments(data)],
                  ),
                ]
              : [
                  ...(data.print_object.with_withdrawal &&
                  data.print_object.payment_method?.name === "Dinheiro"
                    ? [
                        PrintTemplate.span(
                          "Pagamento na Entrega",
                          "font--size-11 font--weight-700 font--ls-22",
                          { style: "width: 100%; text-align: center;" },
                        ),
                      ]
                    : [
                        ...(valueDelivery
                          ? []
                          : [
                              PrintTemplate.span(
                                "Pagamento na Entrega",
                                "font--size-11 font--weight-700 font--ls-22",
                                { style: "width: 100%; text-align: center;" },
                              ),
                            ]),
                      ]),

                  PrintTemplate.flexContainer(
                    "column",
                    "2px",
                    "center",
                    "center",
                    "width: 100%; padding-bottom: 2px; border-bottom: 1px dashed #000;",
                    [
                      PrintTemplate.flexContainer(
                        "row",
                        "2px",
                        "flex-start",
                        "none",
                        "width: 100%;",
                        [
                          PrintTemplate.span(
                            "Forma de pgto:",
                            "font--size-10 font--weight-700 font--ls-20",
                          ),
                          ...("payment_method" in data.print_object &&
                          data.print_object.payment_method
                            ? [
                                PrintTemplate.span(
                                  `${data.print_object.payment_method.name}`,
                                  "font--size-9 font--weight-500 font--ls-18",
                                ),
                              ]
                            : [
                                PrintTemplate.span(
                                  `Pagamento não informado`,
                                  "font--size-9 font--weight-500 font--ls-18",
                                ),
                              ]),
                        ],
                      ),
                      PrintTemplate.flexContainer(
                        "row",
                        "0px",
                        "flex-start",
                        "space-between",
                        "width: 100%;",
                        [
                          PrintTemplate.span(
                            "VALOR TOTAL DO PEDIDO",
                            "font--size-10 font--weight-500 font--ls-20",
                          ),
                          PrintTemplate.span(
                            `R$ ${Number(valueTotal).toFixed(2).replace(".", ",")}`,
                            "font--size-10 nowrap font--weight-700 font--ls-20",
                          ),
                        ],
                      ),
                      ...(data.print_object.discount_total &&
                      data.print_object.discount_total?.length > 0
                        ? [
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%;",
                              [
                                PrintTemplate.span(
                                  `Cupom/Desconto`,
                                  "font--size-10 font--weight-500 font--ls-18",
                                ),
                                PrintTemplate.span(
                                  `R$ -${data.print_object.discount_total.replace(".", ",")}`,
                                  "font--size-9 font--weight-500 font--ls-18",
                                ),
                              ],
                            ),
                          ]
                        : []),

                      // ...(cupomIfood ? [
                      //   this.flexContainer('row', '0px', 'flex-start', 'space-between', 'width: 100%;', [
                      //     this.span(`Cupom Ifood`, 'font--size-10 font--weight-500 font--ls-18'),
                      //     this.span(`R$ -${data.print_object.ifood_discount?.replace('.', ',') || '0,00'}`, 'font--size-9 font--weight-500 font--ls-18')
                      //   ])
                      // ] :
                      //   []
                      // ),
                      ...(taxServiceIfood
                        ? [
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%;",
                              [
                                PrintTemplate.span(
                                  `Taxa de Serviço iFood`,
                                  "font--size-10 font--weight-500 font--ls-18",
                                ),
                                PrintTemplate.span(
                                  `R$ +${data.print_object.additional_fees?.replace(".", ",") || "0,00"}`,
                                  "font--size-9 font--weight-500 font--ls-18",
                                ),
                              ],
                            ),
                          ]
                        : []),
                      ...("is_delivery" in data.print_object &&
                      "with_withdrawal" in data.print_object &&
                      data.print_object.is_delivery &&
                      !data.print_object.with_withdrawal
                        ? [
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%;",
                              [
                                PrintTemplate.span(
                                  "Taxa de Entrega",
                                  "font--size-10 font--weight-500 font--ls-20",
                                ),
                                PrintTemplate.span(
                                  `R$ ${data.print_object.delivery_tax_price?.replace(".", ",")}`,
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                              ],
                            ),
                          ]
                        : []),

                      ...(valueDelivery &&
                      data.print_object.payment_method?.name !== "Dinheiro"
                        ? [
                            PrintTemplate.flexContainer(
                              "column",
                              "0px",
                              "center",
                              "space-between",
                              "width: 100%; padding-top: 2px; border-top: 1px dashed #000;",
                              [
                                PrintTemplate.flexContainer(
                                  "row",
                                  "0px",
                                  "center",
                                  "space-between",
                                  "width: 100%; padding-bottom: 4px;",
                                  [
                                    PrintTemplate.span(
                                      "VALOR PAGO",
                                      "font--size-10 font--weight-700 font--ls-20",
                                    ),
                                    cupomIfood
                                      ? PrintTemplate.span(
                                          `R$ ${totalPaidIfood}`,
                                          "font--size-10 font--weight-700 font--ls-20",
                                        )
                                      : PrintTemplate.span(
                                          `R$ ${data.print_object.total_paid.toFixed(2).replace(".", ",")}`,
                                          "font--size-10 font--weight-700 font--ls-20",
                                        ),
                                  ],
                                ),
                                PrintTemplate.span(
                                  "NÃO COBRAR DO CLIENTE",
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                              ],
                            ),
                          ]
                        : [
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%; border-top: 1px dashed #000; padding-top: 2px",
                              [
                                PrintTemplate.span(
                                  "COBRAR DO CLIENTE",
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                                ...(data.print_object.ifood_discount
                                  ? [
                                      PrintTemplate.span(
                                        `R$ ${(Number(data.print_object.total_delivery_price) - Number(data.print_object.total_paid)).toFixed(2).replace(".", ",")}`,
                                        "font--size-10 font--weight-700 font--ls-20",
                                      ),
                                    ]
                                  : [
                                      PrintTemplate.span(
                                        `R$ ${String(data.print_object.total_delivery_price)?.replace(".", ",")}`,
                                        "font--size-10 font--weight-700 font--ls-20",
                                      ),
                                    ]),
                              ],
                            ),
                          ]),

                      ...("payment_method" in data.print_object &&
                      data.print_object.payment_method?.name === "Dinheiro"
                        ? [
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%; padding-top: 2px; border-top: 1px dashed #000;",
                              [
                                PrintTemplate.span(
                                  "RECEBER",
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                                PrintTemplate.span(
                                  `R$ ${data.print_object.user_change.replace(".", ",") || "0,00"}`,
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                              ],
                            ),
                            PrintTemplate.flexContainer(
                              "row",
                              "0px",
                              "flex-start",
                              "space-between",
                              "width: 100%;",
                              [
                                PrintTemplate.span(
                                  "TROCO",
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                                PrintTemplate.span(
                                  `R$ ${(Number(data.print_object.user_change) - Number(data.print_object.total_service_price)).toFixed(2).replace(".", ",")}`,
                                  "font--size-10 font--weight-700 font--ls-20",
                                ),
                              ],
                            ),
                          ]
                        : []),
                    ],
                  ),
                ]),
          ],
        );
      }
      case "order": {
        return PrintTemplate.div({});
      }
    }
    return PrintTemplate.div({});
  }
}
